import { getConversationMessages, sendMessage, callAI, type ChatMessage } from '../lib/chat';
import { getCompanionName } from '../lib/supabase';

export class ChatModal {
  private overlay: HTMLElement;
  private modal: HTMLElement;
  private messagesContainer: HTMLElement;
  private inputArea: HTMLTextAreaElement;
  private sendButton: HTMLButtonElement;
  private conversationId: string | null = null;
  private userId: string | null = null;
  private companionName: string = 'Emi';
  private onClose?: () => void;

  constructor(userId: string, onClose?: () => void) {
    this.userId = userId;
    this.onClose = onClose;
    this.overlay = this.createOverlay();
    this.modal = this.createModal();
    this.messagesContainer = this.modal.querySelector('#chat-modal-messages')!;
    this.inputArea = this.modal.querySelector('#chat-modal-input')!;
    this.sendButton = this.modal.querySelector('#chat-modal-send')!;

    this.setupEventListeners();
    this.loadCompanionName();
  }

  private async loadCompanionName() {
    this.companionName = await getCompanionName();
    // Update the title if modal is already open
    const titleEl = this.modal.querySelector('#chat-modal-title');
    if (titleEl && titleEl.textContent?.includes('Emi')) {
      titleEl.textContent = titleEl.textContent.replace('Emi', this.companionName);
    }
  }

  private createOverlay(): HTMLElement {
    const overlay = document.createElement('div');
    overlay.id = 'chat-modal-overlay';
    overlay.className = 'chat-modal-overlay';
    overlay.innerHTML = '';
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.5);
      z-index: 9998;
      display: none;
      backdrop-filter: blur(4px);
    `;
    document.body.appendChild(overlay);
    return overlay;
  }

  private createModal(): HTMLElement {
    const modal = document.createElement('div');
    modal.id = 'chat-modal';
    modal.className = 'chat-modal';
    modal.innerHTML = `
      <div class="chat-modal-header">
        <h3 id="chat-modal-title">Chat</h3>
        <button id="chat-modal-close" class="chat-modal-close-btn">✕</button>
      </div>
      <div id="chat-modal-messages" class="chat-modal-messages"></div>
      <div class="chat-modal-input-area">
        <textarea
          id="chat-modal-input"
          class="chat-modal-textarea"
          placeholder="Type your message..."
          rows="3"
        ></textarea>
        <button id="chat-modal-send" class="chat-modal-send-btn">
          <span class="send-icon">→</span>
        </button>
      </div>
    `;

    modal.style.cssText = `
      position: fixed;
      right: 0;
      top: 0;
      bottom: 0;
      width: 90%;
      max-width: 500px;
      background: #fff;
      border-left: 3px solid #000;
      box-shadow: -8px 0 24px rgba(0, 0, 0, 0.2);
      z-index: 9999;
      display: none;
      flex-direction: column;
      transform: translateX(100%);
      transition: transform 0.3s ease;
    `;

    document.body.appendChild(modal);
    return modal;
  }

  private setupEventListeners(): void {
    // Close button
    const closeBtn = this.modal.querySelector('#chat-modal-close')!;
    closeBtn.addEventListener('click', () => this.close());

    // Overlay click to close
    this.overlay.addEventListener('click', () => this.close());

    // Send button
    this.sendButton.addEventListener('click', () => this.handleSend());

    // Enter to send (Shift+Enter for new line)
    this.inputArea.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        this.handleSend();
      }
    });

    // Auto-resize textarea
    this.inputArea.addEventListener('input', () => {
      this.inputArea.style.height = 'auto';
      this.inputArea.style.height = Math.min(this.inputArea.scrollHeight, 120) + 'px';
    });
  }

  private async handleSend(): Promise<void> {
    const content = this.inputArea.value.trim();
    if (!content || !this.conversationId) return;

    // Clear input
    this.inputArea.value = '';
    this.inputArea.style.height = 'auto';

    // Add user message to UI
    this.addMessage({ role: 'user', content });

    // Disable send button and show typing indicator
    this.sendButton.disabled = true;
    this.showTypingIndicator();

    // Call AI
    const result = await callAI(this.conversationId, content);

    // Remove typing indicator
    this.removeTypingIndicator();
    this.sendButton.disabled = false;

    if (result) {
      this.addMessage({ role: 'assistant', content: result.reply });
    } else {
      this.addMessage({
        role: 'assistant',
        content: 'Sorry, I had trouble responding. Please try again.'
      });
    }

    // Re-focus input
    this.inputArea.focus();
  }

  private addMessage(message: { role: 'user' | 'assistant'; content: string }): void {
    const messageEl = document.createElement('div');
    messageEl.className = `chat-message ${message.role === 'user' ? 'user' : 'emi'}`;

    const avatar = document.createElement('div');
    avatar.className = 'chat-avatar';
    avatar.textContent = message.role === 'user' ? 'You' : this.companionName;

    const bubble = document.createElement('div');
    bubble.className = 'chat-bubble';

    const text = document.createElement('p');
    text.className = 'chat-text';
    text.textContent = message.content;

    bubble.appendChild(text);
    messageEl.appendChild(avatar);
    messageEl.appendChild(bubble);

    this.messagesContainer.appendChild(messageEl);
    this.scrollToBottom();
  }

  private showTypingIndicator(): void {
    const indicator = document.createElement('div');
    indicator.id = 'typing-indicator';
    indicator.className = 'chat-message emi';
    indicator.innerHTML = `
      <div class="chat-avatar">${this.companionName}</div>
      <div class="chat-bubble">
        <div class="typing-indicator">
          <div class="typing-dot"></div>
          <div class="typing-dot"></div>
          <div class="typing-dot"></div>
        </div>
      </div>
    `;
    this.messagesContainer.appendChild(indicator);
    this.scrollToBottom();
  }

  private removeTypingIndicator(): void {
    const indicator = document.getElementById('typing-indicator');
    if (indicator) {
      indicator.remove();
    }
  }

  private scrollToBottom(): void {
    this.messagesContainer.scrollTop = this.messagesContainer.scrollHeight;
  }

  public async open(conversationId: string, title: string = 'Chat with Emi'): Promise<void> {
    this.conversationId = conversationId;

    // Update title
    const titleEl = this.modal.querySelector('#chat-modal-title')!;
    titleEl.textContent = title;

    // Clear previous messages
    this.messagesContainer.innerHTML = '';

    // Load conversation history
    const messages = await getConversationMessages(conversationId);
    messages.forEach(msg => {
      if (msg.role !== 'system') {
        this.addMessage({ role: msg.role as 'user' | 'assistant', content: msg.content });
      }
    });

    // Show modal
    this.overlay.style.display = 'block';
    this.modal.style.display = 'flex';

    // Trigger animation
    setTimeout(() => {
      this.modal.style.transform = 'translateX(0)';
    }, 10);

    // Focus input
    setTimeout(() => {
      this.inputArea.focus();
    }, 350);
  }

  public close(): void {
    this.modal.style.transform = 'translateX(100%)';

    setTimeout(() => {
      this.overlay.style.display = 'none';
      this.modal.style.display = 'none';
      this.conversationId = null;

      if (this.onClose) {
        this.onClose();
      }
    }, 300);
  }

  public destroy(): void {
    this.overlay.remove();
    this.modal.remove();
  }
}

/**
 * Add required styles to the document
 */
export function injectChatModalStyles(): void {
  if (document.getElementById('chat-modal-styles')) return;

  const style = document.createElement('style');
  style.id = 'chat-modal-styles';
  style.textContent = `
    .chat-modal-header {
      padding: 20px 24px;
      border-bottom: 2px solid #000;
      display: flex;
      justify-content: space-between;
      align-items: center;
      background: #fff;
    }

    .chat-modal-header h3 {
      margin: 0;
      font-size: 20px;
      font-weight: 700;
    }

    .chat-modal-close-btn {
      background: none;
      border: 2px solid #000;
      border-radius: 8px;
      width: 36px;
      height: 36px;
      cursor: pointer;
      font-size: 20px;
      font-weight: 700;
      line-height: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s ease;
    }

    .chat-modal-close-btn:hover {
      background: rgba(0, 0, 0, 0.05);
      transform: scale(1.05);
    }

    .chat-modal-messages {
      flex: 1;
      overflow-y: auto;
      padding: 20px;
      display: flex;
      flex-direction: column;
      gap: 16px;
      background: #fafafa;
    }

    .chat-modal-messages::-webkit-scrollbar {
      width: 8px;
    }

    .chat-modal-messages::-webkit-scrollbar-track {
      background: #f1f1f1;
    }

    .chat-modal-messages::-webkit-scrollbar-thumb {
      background: #888;
      border-radius: 4px;
    }

    .chat-modal-messages::-webkit-scrollbar-thumb:hover {
      background: #555;
    }

    .chat-modal-input-area {
      padding: 16px;
      border-top: 2px solid #000;
      display: flex;
      gap: 12px;
      align-items: flex-end;
      background: #fff;
    }

    .chat-modal-textarea {
      flex: 1;
      padding: 12px 14px;
      border: 2px solid #000;
      border-radius: 12px;
      font-size: 15px;
      font-family: inherit;
      resize: none;
      min-height: 44px;
      max-height: 120px;
      background: #fff;
      line-height: 1.4;
    }

    .chat-modal-textarea:focus {
      outline: none;
      border-color: #3DDAB4;
    }

    .chat-modal-send-btn {
      width: 44px;
      height: 44px;
      background: #3DDAB4;
      color: #0b1f18;
      border: 2px solid #000;
      border-radius: 50%;
      cursor: pointer;
      box-shadow: 0 3px 0 #000;
      transition: all 0.08s ease;
      font-family: inherit;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    .chat-modal-send-btn:hover:not(:disabled) {
      transform: translateY(1px);
      box-shadow: 0 2px 0 #000;
    }

    .chat-modal-send-btn:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    @media (max-width: 768px) {
      .chat-modal {
        width: 100% !important;
        max-width: 100% !important;
      }
    }
  `;

  document.head.appendChild(style);
}
